import React, { FC, memo, useMemo, ReactNode } from 'react';
import {
  OwcEditableDropdown,
  OwcListItem,
  OwcTypography,
  OwcProgressSpinner
} from '@one/react';
import {
  UserOption,
  buildUserOptions,
  UserOptionsConfig
} from '../../../utils/helpers/user/userOptionsBuilder';
import { UserData } from '../../../utils/helpers/text/userFormatting';

interface UserSelectDropdownProps {
  value: string | UserData | UserOption | null | '';
  users: UserData[];
  onChange: (selected: UserOption | null, user?: UserData) => void;
  optionsConfig?: UserOptionsConfig;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  loadingComponent?: ReactNode;
  clearable?: boolean;
  width?: number | string;
  style?: React.CSSProperties;
  dataTestid?: string;
  name?: string;
  dropdownProps?: Record<string, any>;
  emptyComponent?: ReactNode;
  emptyText?: string;
  returnUserData?: boolean;
  keyExtractor?: (option: UserOption, index: number) => string;
}

/**
 * Generic User Selection Dropdown
*/
const UserSelectDropdown: FC<UserSelectDropdownProps> = memo(({
  value,
  users,
  onChange,
  optionsConfig,
  label = "Select user",
  placeholder = "Type to search users",
  required = false,
  disabled = false,
  loading = false,
  loadingText = "Loading...",
  loadingComponent,
  clearable = true,
  width = 200,
  style = {},
  dataTestid = "user-select-dropdown",
  name = "userSelect",
  dropdownProps = {},
  emptyComponent,
  emptyText = "No users available",
  returnUserData = true,
  keyExtractor
}) => {
  const options = useMemo(() => {
    if (!users || users.length === 0) return [];
    return buildUserOptions(users, optionsConfig);
  }, [users, optionsConfig]);

  const getDisplayValue = (): string | null => {
    if (!value) return null;

    if (typeof value === 'string') {
      if (value === '') {
        const allUsersOption = options.find(opt => opt.name === '');
        return allUsersOption?.label || null;
      }
      const option = options.find(opt => opt.name === value);
      return option?.label || value;
    }

    if (typeof value === 'object') {
      if ('label' in value && value.label) {
        return value.label;
      }
      if ('userId' in value && value.userId) {
        const option = options.find(opt => opt.name === value.userId);
        return option?.label || null;
      }
    }

    return null;
  };

  const isOptionSelected = (option: UserOption): boolean => {
    if (!value) return false;

    if (typeof value === 'string') {
      return option.name === value;
    }

    if (typeof value === 'object' && 'userId' in value) {
      return option.name === (value as UserData).userId;
    }

    if (typeof value === 'object' && 'name' in value) {
      return option.name === (value as UserOption).name;
    }

    return false;
  };

  const handleSelectChange = (ev: any) => {
    if (ev.detail !== null && ev.detail !== undefined) {
      const selectedOption = options[ev.detail];
      if (selectedOption) {
        const userData = returnUserData && selectedOption.name
          ? users.find(u => u.userId === selectedOption.name)
          : undefined;

        onChange(selectedOption, userData);
      }
    } else if (clearable) {
      onChange(null);
    }
  };

  const handleInputKeyDown = (ev: any) => {
    const inputValue = ev.target?.value || "";
    if (clearable && inputValue.trim() === "" && (ev.detail === "Enter" || ev.detail === "Tab")) {
      onChange(null);
    }
  };

  const getItemKey = (option: UserOption, index: number): string => {
    if (keyExtractor) {
      return keyExtractor(option, index);
    }
    return option.name || `option-${index}`;
  };

  if (loading) {
    return (
      <OwcEditableDropdown
        placeholder={placeholder}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        style={{ width, ...style }}
        label={label}
        data-testid={dataTestid}
        name={name}
        value={loadingText}
        disabled={true}
        {...dropdownProps}
      >
        {loadingComponent || (
          <OwcListItem value={loadingText} style={{ marginLeft: "auto", marginRight: "auto" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <OwcTypography variant="caption" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <OwcProgressSpinner diameter={24} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </OwcTypography>
          </OwcListItem>
        )}
      </OwcEditableDropdown>
    );
  }

  if ((!options || options.length === 0) && emptyComponent) {
    return (
      <OwcEditableDropdown
        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} style={{ width, ...style }}
        label={label}
        data-testid={dataTestid}
        name={name}
        value=""
        disabled={true}
        {...dropdownProps}>
        {emptyComponent}
      </OwcEditableDropdown>
    );
  }

  if (!options || options.length === 0) {
    return (
      <OwcEditableDropdown
        placeholder={placeholder} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} style={{ width, ...style }}
        label={label}
        data-testid={dataTestid}
        name={name}
        value=""
        disabled={true}
        {...dropdownProps}>
        <OwcListItem value={emptyText} disabled placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <OwcTypography variant="caption" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{emptyText}</OwcTypography>
        </OwcListItem>
      </OwcEditableDropdown>
    );
  }

  return (
    <OwcEditableDropdown
      onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} style={{ width, ...style }}
      label={label}
      data-testid={dataTestid}
      name={name}
      value={getDisplayValue() || undefined}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      onSelectChange={handleSelectChange}
      onInputKeyDown={handleInputKeyDown}
      {...dropdownProps}    >
      {options.map((option, index) => (
        <OwcListItem
          key={getItemKey(option, index)}
          value={option.label}
          selected={isOptionSelected(option)}
          data-testid={`${dataTestid}-item-${option.name || 'empty'}`}
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          {option.label}
        </OwcListItem>
      ))}
    </OwcEditableDropdown>
  );
});

export default UserSelectDropdown;
